import { useQueryClient, useMutation, UseMutationResult } from "@tanstack/react-query";
import { useAxios } from "../useAxios";
import { getUseProject } from "./useProjectList";
import { transformProjectArray } from "./projectTransform";
import { useCallback, useMemo } from "react";
import {
  ProjectFilters,
  ProjectCounts,
  ProjectPayload,
  ProjectState,
} from "hooks/useProjects/useProjects.types";
import { ApiErrorCode, USER_API } from "config";
import { CreateProjectDataType } from "store/types/project";
import { handleToast } from "utils/toasts";
import { projectKeys } from "../../queries/project.keys";
import { catalogKeys } from "../../queries/profile.keys";
import { updateCatalogInCache } from "hooks/useProfile/useProfileCache";
import { useUserCatalog } from "hooks/useSession";
import { useAnalytics } from "hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "analytics";
import type { UserCatalog } from "types/user-catalog";
import { clearLastProjectLocation } from "components/layout/shell/AppSidebar/lastProjectLocation";

type UpdateProjectPayload = Partial<CreateProjectDataType> & Record<string, unknown>;

const PROJECT_CREATE_TIMEOUT_MS = 5 * 60 * 1000;

const updateCatalogProject = (
  catalog: UserCatalog,
  projectId: string | number,
  updater: (
    project: UserCatalog["workspaces"][number]["projects"][number]
  ) => UserCatalog["workspaces"][number]["projects"][number]
): UserCatalog => ({
  ...catalog,
  workspaces: catalog.workspaces.map((workspace) => ({
    ...workspace,
    projects: workspace.projects.map((project) =>
      String(project.project_id) === String(projectId) ? updater(project) : project
    ),
  })),
});

const removeCatalogProject = (catalog: UserCatalog, projectId: string | number): UserCatalog => ({
  ...catalog,
  workspaces: catalog.workspaces.map((workspace) => ({
    ...workspace,
    projects: workspace.projects.filter(
      (project) => String(project.project_id) !== String(projectId)
    ),
  })),
});

export interface UseProjectsApiResult {
  // Query results
  projects: ProjectState[];
  rawProjects: ProjectPayload[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  pageCount: number;
  totalCount: number;

  // Pagination
  hasMoreProjects: boolean;
  loadMoreProjects: () => void;
  isLoadingMore: boolean;

  // Counts
  projectCounts: ProjectCounts;
  projectsCount: number; // Added projects count for stats

  // Query hooks and utilities
  useProject: (
    projectId?: string | number,
    enablePolling?: boolean
  ) => ReturnType<typeof getUseProject>;
  getProjects: (filter?: (project: ProjectPayload) => boolean) => ProjectPayload[];
  getProjectById: (projectId: string | number) => Promise<ProjectState>;
  refetch: () => Promise<any>;

  // Mutations
  createProject: UseMutationResult<unknown, unknown, CreateProjectDataType, unknown>;
  updateProject: UseMutationResult<
    unknown,
    unknown,
    { projectId: string | number; data: UpdateProjectPayload },
    unknown
  >;
  archiveProject: UseMutationResult<unknown, unknown, string | number, unknown>;
  unarchiveProject: UseMutationResult<unknown, unknown, string | number, unknown>;
  deleteProject: UseMutationResult<unknown, unknown, string | number, unknown>;
  upgradeProject: UseMutationResult<unknown, unknown, string | number, unknown>;
}

/**
 * Main hook that brings together all project API functionality
 */
export const useProjectsApi = (filters: ProjectFilters = {}) => {
  const scopedWorkspaceId = filters.workspaceId ? String(filters.workspaceId) : null;
  const queryClient = useQueryClient();
  const axios = useAxios({ allResponse: false });
  const {
    data: catalog,
    isLoading: isCatalogLoading,
    isFetching: isCatalogFetching,
    isError: isCatalogError,
    error: catalogError,
  } = useUserCatalog();
  // Loading only until the catalog first resolves; background refetches must
  // not flip pages back to their loading state once data is on screen.
  const isProjectListLoading = !catalog && (isCatalogLoading || isCatalogFetching);
  // Same rule for failures: a failed refetch keeps the last list on screen.
  const isProjectListError = !catalog && isCatalogError;
  const { trackEvent } = useAnalytics();

  // Extract projects from catalog data (source of truth for project lists)
  const profileProjects = useMemo(() => {
    if (!catalog?.workspaces) return [];

    const allProjects: ProjectPayload[] = [];

    catalog.workspaces.forEach((workspace) => {
      if (scopedWorkspaceId && String(workspace.workspace_id) !== scopedWorkspaceId) return;
      if (workspace.projects) {
        workspace.projects.forEach((project) => {
          // Convert profile project data to ProjectPayload format
          const projectPayload: ProjectPayload = {
            project_id: project.project_id,
            name: project.name,
            description: undefined, // Not available in profile data
            production_type: project.production_type,
            slug: project.slug,
            project_image_path: project.project_image_path ?? undefined,
            created_at: project.created_at,
            archived: project.archived,
            deleted_at: undefined,
            is_deleted: false,
            status: project.status,
            workspace_id: workspace.workspace_id,
            purchase_type: "", // Profile data doesn't include purchase_type - will be determined by isProjectTrial
            one_time_purchase_id: 0,
            trial_expiry_date: project.trial_expiry_date || undefined,
            is_demo: (project as { is_demo?: boolean }).is_demo,
            versions_count: undefined,
            active_document_id: (project as { active_document_id?: string }).active_document_id,
            versions: undefined, // Will be fetched when needed
            features: {
              project_feature_id: 0,
              project_id: project.project_id,
              has_coverage: false,
              has_character_breakdown: false,
              has_script_breakdown: false,
              has_budget: false,
              has_schedule: false,
              schedule_start_date: "",
              min_budget: undefined,
              max_budget: undefined,
              production_state: null,
            },
            feature_statuses: [],
            team: undefined,
            user: undefined,
            current_user_role: "viewer", // Default - will be updated when individual project is fetched
            permissions: undefined, // Will be fetched when individual project is accessed
          };

          allProjects.push(projectPayload);
        });
      }
    });

    return allProjects;
  }, [catalog?.workspaces, scopedWorkspaceId]);

  // Use catalog projects as the source of truth
  const allProjects = profileProjects;

  // Transform all projects for UI
  const allProjectsTransformed = useMemo(() => transformProjectArray(allProjects), [allProjects]);

  // Derive projects count from combined project list
  const projectsCount = allProjectsTransformed.length;

  // Calculate counts
  const projectCounts: ProjectCounts = useMemo(() => {
    const active = allProjectsTransformed.filter(
      (project) => !project.archived && !project.deletedAt && !project.isDeleted
    ).length;
    const inactive = allProjectsTransformed.filter(
      (project) => project.archived || project.deletedAt || project.isDeleted
    ).length;

    return {
      total: allProjectsTransformed.length,
      active,
      inactive,
    };
  }, [allProjectsTransformed]);

  // Create the per-project hook factory
  const useProject = getUseProject(axios);

  // Get a project by ID (for one-time use)
  const getProjectById = useCallback(
    async (projectId: string | number) => {
      try {
        const data = (await axios.get(`${USER_API.projects}/${projectId}`)).data;
        return transformProjectArray([data])[0];
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [axios]
  );

  // Create project mutation - supports new response and error handling
  const createProject = useMutation({
    mutationFn: (data: CreateProjectDataType) =>
      axios.post(USER_API.projects, data, {
        timeout: PROJECT_CREATE_TIMEOUT_MS,
      }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
      const payload = (res?.data?.data ?? res?.data ?? res) as any;
      const isNewProject = Boolean(payload?.isNewProject);
      handleToast(
        isNewProject ? "Project created successfully" : "New document version created successfully",
        "success"
      );
    },
    onError: (err: any) => {
      const httpStatus = err?.response?.status;
      const message: string | undefined = err?.response?.data?.message || err?.message;
      const code: string | undefined = err?.response?.data?.code;
      if (httpStatus === 403) {
        switch (code) {
          case ApiErrorCode.PROJECT_LIMIT_REACHED:
            handleToast(
              "You have reached your project limit. Please purchase additional licenses to create more projects.",
              "error"
            );
            break;
          default: {
            if (message?.includes("one-time purchase licenses")) {
              handleToast(
                "You have exhausted all your one-time purchase licenses. Please purchase more licenses to continue.",
                "error"
              );
            } else if (message?.includes("trial project")) {
              handleToast(message, "error");
            } else {
              const msg = message || "Failed to create project";
              handleToast(msg, "error");
            }
          }
        }
        return;
      }
      handleToast("Failed to create project", "error");
    },
  });

  // Update project mutation
  const updateProject = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string | number; data: any }) =>
      axios.patch(`${USER_API.projects}/${projectId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
      handleToast("Project updated successfully", "success");
    },
    onError: () => {
      handleToast("Failed to update project", "error");
    },
  });

  // Archive project mutation with optimistic updates
  const archiveProject = useMutation({
    mutationKey: ["archive-project"],
    mutationFn: (projectId: string | number) =>
      axios.patch(`${USER_API.projects}/${projectId}/archive`),
    onMutate: async (projectId: string | number) => {
      await queryClient.cancelQueries({ queryKey: catalogKeys.me() });

      const mutationCache = queryClient.getMutationCache();
      const pendingUnarchive = mutationCache
        .getAll()
        .find(
          (mutation) =>
            mutation.options.mutationKey?.[0] === "unarchive-project" &&
            mutation.state.status === "loading"
        );
      if (pendingUnarchive) {
        mutationCache.remove(pendingUnarchive);
      }

      const previousCatalog = queryClient.getQueryData<UserCatalog>(catalogKeys.me());
      updateCatalogInCache(queryClient, (currentCatalog) =>
        updateCatalogProject(currentCatalog, projectId, (project) => ({
          ...project,
          archived: true,
        }))
      );
      return { previousCatalog };
    },
    onError: (error, projectId, context) => {
      if (context?.previousCatalog) {
        queryClient.setQueryData(catalogKeys.me(), context.previousCatalog);
      }
      handleToast("Failed to archive project", "error");
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      handleToast("Project archived successfully", "success");
      trackEvent(ANALYTICS_EVENTS.PROJECT_ARCHIVED, {
        project_id: String(projectId),
      });
    },
  });

  // Unarchive project mutation with optimistic updates
  const unarchiveProject = useMutation({
    mutationKey: ["unarchive-project"],
    mutationFn: (projectId: string | number) =>
      axios.patch(`${USER_API.projects}/${projectId}/unarchive`),
    onMutate: async (projectId: string | number) => {
      await queryClient.cancelQueries({ queryKey: catalogKeys.me() });

      const mutationCache = queryClient.getMutationCache();
      const pendingArchive = mutationCache
        .getAll()
        .find(
          (mutation) =>
            mutation.options.mutationKey?.[0] === "archive-project" &&
            mutation.state.status === "loading"
        );
      if (pendingArchive) {
        mutationCache.remove(pendingArchive);
      }

      const previousCatalog = queryClient.getQueryData<UserCatalog>(catalogKeys.me());
      updateCatalogInCache(queryClient, (currentCatalog) =>
        updateCatalogProject(currentCatalog, projectId, (project) => ({
          ...project,
          archived: false,
        }))
      );
      return { previousCatalog };
    },
    onError: (error, projectId, context) => {
      if (context?.previousCatalog) {
        queryClient.setQueryData(catalogKeys.me(), context.previousCatalog);
      }
      handleToast("Failed to unarchive project", "error");
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      handleToast("Project unarchived successfully", "success");
      trackEvent(ANALYTICS_EVENTS.PROJECT_UNARCHIVED, {
        project_id: String(projectId),
      });
    },
  });

  // Delete project mutation with optimistic profile cache update
  const deleteProject = useMutation({
    mutationKey: ["delete-project"],
    mutationFn: (projectId: string | number) => axios.delete(`${USER_API.projects}/${projectId}`),
    onMutate: async (projectId: string | number) => {
      await queryClient.cancelQueries({ queryKey: catalogKeys.me() });

      const previousCatalog = queryClient.getQueryData<UserCatalog>(catalogKeys.me());
      updateCatalogInCache(queryClient, (currentCatalog) =>
        removeCatalogProject(currentCatalog, projectId)
      );
      return { previousCatalog };
    },
    onError: (_error, _projectId, context) => {
      if (context?.previousCatalog) {
        queryClient.setQueryData(catalogKeys.me(), context.previousCatalog);
      }
      handleToast("Failed to delete project", "error");
    },
    onSuccess: async (_data, projectId) => {
      const deletedDetails = {
        predicate: (query: { queryKey: readonly unknown[] }) =>
          query.queryKey[0] === "projects" &&
          query.queryKey[1] === "detail" &&
          String(query.queryKey[2]) === String(projectId),
      };
      await queryClient.cancelQueries(deletedDetails);
      queryClient.removeQueries(deletedDetails);
      clearLastProjectLocation(projectId);
      queryClient.invalidateQueries({ queryKey: catalogKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      handleToast("Project deleted successfully", "success");
      trackEvent(ANALYTICS_EVENTS.PROJECT_DELETED, {
        project_id: String(projectId),
      });
    },
  });

  // Upgrade project mutation
  const upgradeProject = useMutation({
    mutationFn: (projectId: string | number) =>
      axios.post(`${USER_API.projects}/${projectId}/upgrade`),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      handleToast("Project upgraded successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to upgrade project. Please try again.";
      handleToast(message, "error");
    },
  });

  // Return all functionality with memoization
  return useMemo(
    () => ({
      // Query results
      projects: allProjectsTransformed,
      rawProjects: allProjects,
      isLoading: isProjectListLoading,
      isError: isProjectListError,
      error: isProjectListError ? catalogError : null,
      totalCount: allProjectsTransformed.length,

      // Counts
      projectCounts,
      projectsCount,

      // Query hooks and utilities
      useProject,
      getProjectById,
      refetch: () => Promise.resolve(), // No-op since we use profile data

      // Mutations
      createProject,
      updateProject,
      archiveProject,
      unarchiveProject,
      deleteProject,
      upgradeProject,
    }),
    [
      allProjectsTransformed,
      allProjects,
      isProjectListLoading,
      isProjectListError,
      catalogError,
      projectCounts,
      projectsCount,
      // Removed useProject and getProjectById from dependency array to prevent infinite re-renders
      createProject,
      updateProject,
      archiveProject,
      unarchiveProject,
      deleteProject,
      upgradeProject,
    ]
  );
};

// Re-export types and utilities
export * from "hooks/useProjects/useProjects.types";
export * from "./projectTransform";
export * from "./useVersions";
export * from "./useUpgradeProject";
